import React, { useCallback, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Modal from 'react-modal';
import Button from 'react-bootstrap/Button';
import {
  FiExternalLink, FiChevronDown,
  FiFileText, FiGlobe, FiActivity, FiPieChart, FiUpload, FiDownload,
  FiAlertCircle, FiTerminal, FiShield,
} from 'react-icons/fi';
import Zapi from '../../components/api/api';
import Zcompnfe from '../../components/api/compnfe';
import Zmonitoramento from '../monitoramento/monitoramento';
import ListaRegistros from '../pdvexception/list';
import ListaRegistrosDev from '../pdvexception/listdev';
import LogContingencia, { ContingenciaBadge } from '../pdvexception/contingenciaLog';
import LogCertificado, { CertificadoBadge } from '../pdvexception/certificadoLog';
import FaltantesNFeNFCe, { FaltantesBadge } from '../pdvexception/faltantes';
import ZDashboard from '../monitoramento/dashboard';
import ZUpload from '../monitoramento/upload';
import ZDownload from '../monitoramento/download';
import ServidoresAWS from '../monitoramento/servidoresaws';
import Menu from '../menu';
import DashboardKPIs from './DashboardKPIs';
import { MonitoramentoProvider } from '../../context/MonitoramentoContext';

Modal.setAppElement('#root');

const COMPSOFT_ITEMS = [
  { label: 'Compnfe/CTe', icon: <FiFileText size={13} />, component: <Zcompnfe /> },
  { label: 'Zaniboni',    icon: <FiGlobe    size={13} />, component: <Zapi /> },
  { label: 'Monitor',     icon: <FiActivity size={13} />, component: <Zmonitoramento /> },
  { label: 'Dashboard',   icon: <FiPieChart size={13} />, component: <ZDashboard /> },
  { label: 'Upload' ,      icon: <FiUpload   size={13} />, component: <ZUpload /> },
  { label: 'Download',    icon: <FiDownload size={13} />, component: <ZDownload /> },
];

export default function Principal() {
  const [clock, setClock]             = useState(() => new Date());
  const [open, setOpen]               = useState({ proprio: true, compsoft: true, navMobile: false });
  const [filtroNivel, setFiltroNivel] = useState(null);
  const [abaExcecoes, setAbaExcecoes] = useState('excecoes'); // 'excecoes' | 'contingencia' | 'certificado' | 'faltantes'
  const location                      = useLocation();
  const tabRefs                       = useRef({});
  const [tabIndicator, setTabIndicator] = useState({ left: 0, width: 0 });

  const selecionarFiltroNivel = useCallback((nivel) => {
    setAbaExcecoes('excecoes');
    setFiltroNivel(nivel);
  }, []);

  const userRole = localStorage.getItem('user-role');
  const isMaster = userRole === 'MASTER';

  const NAV_LINKS = [
    { to: '/',           label: 'Home' },
    { to: '/pdv',        label: 'PDV' },
    { to: '/lics',       label: 'Licenças' },
    { to: '/ver',        label: 'Versões' },
    { to: '/csapi',      label: 'Produtos AZ' },
    ...(isMaster ? [
      { to: '/auditoria', label: 'Auditar' },
      { to: '/update',    label: 'Update' },
    ] : []),
  ];

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const toggle = useCallback(key => {
    setOpen(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  useLayoutEffect(() => {
    const updateIndicator = () => {
      const el = tabRefs.current[abaExcecoes];
      if (el) setTabIndicator({ left: el.offsetLeft, width: el.offsetWidth });
    };
    updateIndicator();
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [abaExcecoes]);

  const openExternalPage = useCallback(() => {
    window.open('https://csdash-alpha.vercel.app/', '_blank', 'noopener,noreferrer');
  }, []);

  const dateStr = clock.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
  const hh = String(clock.getHours()).padStart(2, '0');
  const mm = String(clock.getMinutes()).padStart(2, '0');
  const ss = String(clock.getSeconds()).padStart(2, '0');

  return (
    <>
      <Menu />

      <MonitoramentoProvider>
        <div id="cs-layout">

          {/* Painel esquerdo */}
          <div id="cs-sidebar">
            <div className="d-flex flex-column gap-1 py-1">

              {/* Navegação — visível só no mobile */}
              <div className="cs-mobile-nav">
                <button className="cs-section-toggle" onClick={() => toggle('navMobile')}>
                  Navegação
                  <FiChevronDown size={12} className={`cs-chevron${open.navMobile ? '' : ' cs-chevron-collapsed'}`} />
                </button>
                {open.navMobile && (
                  <div className="cs-card">
                    {NAV_LINKS.map(({ to, label }) => (
                      <Link
                        key={to}
                        to={to}
                        className={`cs-mobile-nav-link${location.pathname === to ? ' cs-mobile-nav-active' : ''}`}
                      >
                        {label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* AWS Próprio */}
              <div>
                <button className="cs-section-toggle" onClick={() => toggle('proprio')}>
                  AWS Próprio
                  <FiChevronDown size={12} className={`cs-chevron${open.proprio ? '' : ' cs-chevron-collapsed'}`} />
                </button>
                {open.proprio && (
                  <div className="cs-card">
                    <ServidoresAWS />
                  </div>
                )}
              </div>

              {/* AWS Compsoft */}
              <div>
                <button className="cs-section-toggle" onClick={() => toggle('compsoft')}>
                  AWS Compsoft
                  <FiChevronDown size={12} className={`cs-chevron${open.compsoft ? '' : ' cs-chevron-collapsed'}`} />
                </button>
                {open.compsoft && (
                  <div className="cs-card">
                    {COMPSOFT_ITEMS.map(({ label, icon, component }) => (
                      <div key={label} className="cs-item">
                        <span className="cs-item-label">
                          <span className="cs-item-icon">{icon}</span>
                          {label}
                        </span>
                        <span>{component}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Compsoft */}
              <div>
                <span className="cs-section-label">Compsoft</span>
                <Button
                  variant="outline-primary"
                  onClick={openExternalPage}
                  className="cs-btn-external"
                >
                  <FiExternalLink size={11} />
                  Acessar CSDash
                </Button>
              </div>

            </div>
          </div>

          {/* Conteúdo principal */}
          <div id="cs-main">
            <div className="cs-page-header">
              <div>
                <span className="cs-page-title">Monitoramento PDV</span>
                <span className="cs-page-subtitle">{dateStr}</span>
              </div>
              <span className="cs-page-clock">
                {hh}<span className="cs-clock-colon">:</span>{mm}<span className="cs-clock-colon">:</span>{ss}
              </span>
            </div>

            <DashboardKPIs filtroNivel={filtroNivel} onFilterNivel={selecionarFiltroNivel} />

            <div className="cs-main-card">
              <div className="cs-list-header cs-list-header-tabs">
                <span
                  className="cs-tab-indicator"
                  style={{ left: tabIndicator.left, width: tabIndicator.width }}
                />
                <button
                  type="button"
                  ref={(el) => { tabRefs.current.excecoes = el; }}
                  className={`cs-tab-btn${abaExcecoes === 'excecoes' ? ' cs-tab-active' : ''}`}
                  onClick={() => setAbaExcecoes('excecoes')}
                >
                  <FiAlertCircle size={13} />
                  Exceções em Produção
                </button>
                <button
                  type="button"
                  ref={(el) => { tabRefs.current.contingencia = el; }}
                  className={`cs-tab-btn${abaExcecoes === 'contingencia' ? ' cs-tab-active' : ''}`}
                  onClick={() => setAbaExcecoes('contingencia')}
                >
                  <FiTerminal size={13} />
                  Log de Contingência
                  <ContingenciaBadge />
                </button>
                <button
                  type="button"
                  ref={(el) => { tabRefs.current.certificado = el; }}
                  className={`cs-tab-btn${abaExcecoes === 'certificado' ? ' cs-tab-active' : ''}`}
                  onClick={() => setAbaExcecoes('certificado')}
                >
                  <FiShield size={13} />
                  Certificado do Portal
                  <CertificadoBadge />
                </button>
                <button
                  type="button"
                  ref={(el) => { tabRefs.current.faltantes = el; }}
                  className={`cs-tab-btn${abaExcecoes === 'faltantes' ? ' cs-tab-active' : ''}`}
                  onClick={() => setAbaExcecoes('faltantes')}
                >
                  <FiFileText size={13} />
                  NFe/NFCe Faltante
                  <FaltantesBadge />
                </button>
                {abaExcecoes === 'excecoes' && filtroNivel && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary cs-filter-chip ms-auto"
                    onClick={() => setFiltroNivel(null)}
                  >
                    Filtro: {filtroNivel} ×
                  </button>
                )}
              </div>
              {abaExcecoes === 'excecoes' && <ListaRegistros filtroNivel={filtroNivel} />}
              {abaExcecoes === 'contingencia' && <LogContingencia />}
              {abaExcecoes === 'certificado' && <LogCertificado />}
              {abaExcecoes === 'faltantes' && <FaltantesNFeNFCe />}
            </div>

            <div className="cs-main-card">
              <div className="cs-list-header">
                <FiTerminal size={13} />
                Desenvolvimento / Teste
                {filtroNivel && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary cs-filter-chip ms-auto"
                    onClick={() => setFiltroNivel(null)}
                  >
                    Filtro: {filtroNivel} ×
                  </button>
                )}
              </div>
              <ListaRegistrosDev filtroNivel={filtroNivel} />
            </div>
          </div>

        </div>
      </MonitoramentoProvider>
    </>
  );
}
